import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Plug, ExternalLink, Check, X, Clock } from 'lucide-react';

export function IntegrationSettings() {
  const [integrations] = useState([
    {
      id: 'google-analytics',
      name: 'Google Analytics',
      description: 'Connect your Google Analytics account for advanced insights',
      connected: true,
      comingSoon: false,
      icon: '📊',
      category: 'Analytics'
    },
    {
      id: 'google-ads',
      name: 'Google Ads',
      description: 'Import campaign performance data and optimize ad spend',
      connected: false,
      comingSoon: true,
      icon: '🎯',
      category: 'Advertising'
    },
    {
      id: 'google-search-console',
      name: 'Google Search Console',
      description: 'Monitor organic search performance and SEO insights',
      connected: false,
      comingSoon: true,
      icon: '🔍',
      category: 'SEO'
    },
    {
      id: 'facebook-ads',
      name: 'Facebook Ads',
      description: 'Track social media campaign performance and ROI',
      connected: false,
      comingSoon: true,
      icon: '📘',
      category: 'Advertising'
    },
    {
      id: 'hubspot',
      name: 'HubSpot',
      description: 'Sync CRM data and track lead conversion metrics',
      connected: false,
      comingSoon: true,
      icon: '🧡',
      category: 'CRM'
    },
    {
      id: 'shopify',
      name: 'Shopify',
      description: 'Connect your e-commerce store for sales analytics',
      connected: false,
      comingSoon: true,
      icon: '🛍️',
      category: 'E-commerce'
    }
  ]);

  // Group integrations by category
  const integrationsByCategory = integrations.reduce((acc, integration) => {
    if (!acc[integration.category]) {
      acc[integration.category] = [];
    }
    acc[integration.category].push(integration);
    return acc;
  }, {} as Record<string, typeof integrations>);

  const getStatusBadge = (integration: typeof integrations[0]) => {
    if (integration.connected) {
      return (
        <div className="flex items-center text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-full">
          <Check className="w-4 h-4 mr-1" />
          <span className="text-sm font-medium">Connected</span>
        </div>
      );
    } else if (integration.comingSoon) {
      return (
        <div className="flex items-center text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-1 rounded-full">
          <Clock className="w-4 h-4 mr-1" />
          <span className="text-sm font-medium">Coming Soon</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20 px-3 py-1 rounded-full">
          <X className="w-4 h-4 mr-1" />
          <span className="text-sm font-medium">Not Connected</span>
        </div>
      );
    }
  };

  const getActionButtons = (integration: typeof integrations[0]) => {
    if (integration.connected) {
      return (
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            Configure
          </Button>
          <Button variant="ghost" size="sm" className="text-red-600 dark:text-red-400">
            Disconnect
          </Button>
        </div>
      );
    } else if (integration.comingSoon) {
      return (
        <Button variant="outline" size="sm" disabled>
          Coming Soon
        </Button>
      );
    } else {
      return (
        <Button size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
          Connect
        </Button>
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Integration Categories */}
      {Object.entries(integrationsByCategory).map(([category, categoryIntegrations]) => (
        <Card key={category} className="p-6">
          <div className="flex items-center mb-6">
            <Plug className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{category} Integrations</h3>
          </div>

          <div className="space-y-4">
            {categoryIntegrations.map((integration) => (
              <div key={integration.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="text-2xl">{integration.icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-1">
                      <h4 className="font-medium text-gray-900 dark:text-white">{integration.name}</h4>
                      {getStatusBadge(integration)}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{integration.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {getActionButtons(integration)}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}

      {/* API Access Section */}
      <Card className="p-6">
        <div className="flex items-center mb-6">
          <ExternalLink className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">API Access</h3>
        </div>
        
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-300">
            Generate API keys to integrate Crutan with your own applications and workflows.
          </p>
          <div className="flex items-center space-x-3">
            <Button variant="outline">
              Manage API Keys
            </Button>
            <Button variant="ghost" size="sm">
              View Documentation
            </Button>
          </div>
        </div>
      </Card>

      {/* Coming Soon Notice */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start space-x-3">
          <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">More Integrations Coming Soon</h4>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              We're actively working on adding more integrations to help you centralize all your marketing data. 
              Have a specific platform you'd like to see? Let us know!
            </p>
            <Button variant="outline" size="sm" className="mt-3 border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-600 dark:text-blue-300 dark:hover:bg-blue-900/30">
              Request Integration
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
} 